// Mirrors backend com.kervolt.oms.approval.dto.ApprovalDtos (FW-3A).
import { RuleKindInfo, RuleMatchMode, BankRuleCondition } from './bank/bankRule.interface';

export type ApproverType = 'USER' | 'ROLE' | 'DEPARTMENT_ADMIN' | 'SUBMITTER_DEPARTMENT_ADMIN';
export type ApprovalRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type ApprovalStepStatus = 'WAITING' | 'APPROVED' | 'REJECTED' | 'SKIPPED';

export const APPROVER_TYPE_LABEL: Record<ApproverType, string> = {
  USER: 'A specific person',
  ROLE: 'Anyone holding a role',
  DEPARTMENT_ADMIN: 'Admin of a department',
  SUBMITTER_DEPARTMENT_ADMIN: "Admin of the submitter's department",
};

export interface ApprovalStepRequest {
  approverType: ApproverType;
  approverUserId?: string | null;
  approverRoleId?: string | null;
  departmentId?: string | null;
  authorityLimit?: number | null;
}

export interface ApprovalWorkflowRequest {
  name: string;
  subjectType: string;
  matchMode: RuleMatchMode;
  priority?: number | null;
  conditions: BankRuleCondition[];
  distinctApprovers: boolean;
  skipSubmitterSteps: boolean;
  steps: ApprovalStepRequest[];
}

export interface ApprovalStepDTO {
  seq: number;
  approverType: ApproverType;
  approverUserId?: string | null;
  approverUserName?: string | null;
  approverRoleId?: string | null;
  approverRoleName?: string | null;
  departmentId?: string | null;
  departmentName?: string | null;
  authorityLimit?: number | null;
}

export interface ApprovalWorkflow {
  rule: {
    id: string;
    institutionId: string;
    subjectType: string;
    name: string;
    priority: number;
    matchMode: RuleMatchMode;
    active: boolean;
    hitCount: number;
    conditions: BankRuleCondition[];
  };
  distinctApprovers: boolean;
  skipSubmitterSteps: boolean;
  steps: ApprovalStepDTO[];
}

export interface ApprovalSubjectType {
  subjectType: string;
  label: string;
  kind: RuleKindInfo;
}

export interface ApprovalOption { id: string; name: string }
export interface ApproverOptions { members: ApprovalOption[]; roles: ApprovalOption[]; departments: ApprovalOption[] }

export interface ApprovalRequestRow {
  id: string;
  institutionId: string;
  subjectType: string;
  subjectLabel: string;
  subjectId: string;
  subjectNumber?: string | null;
  party?: string | null;
  amount?: number | null;
  submittedById: string;
  submittedByName?: string | null;
  submittedAt: string;
  status: ApprovalRequestStatus;
  currentSeq?: number | null;
  totalSteps: number;
  waitingFor: string[];
  workflowName?: string | null;
  decidedAt?: string | null;
}

/** Built-in (non-configurable) approver kinds, e.g. the S1 quotation discount approval. Never offered in the workflow editor. */
export type BuiltInApproverType = 'SALES_TEAM_MANAGER' | 'SALES_DEPARTMENT_HEAD' | 'SALES_SCOPE_APPROVER';

export interface ApprovalRequestStep {
  seq: number;
  approverType: ApproverType | BuiltInApproverType;
  approverLabel: string;
  authorityLimit?: number | null;
  status: ApprovalStepStatus;
  decidedById?: string | null;
  decidedByName?: string | null;
  decidedAt?: string | null;
  comment?: string | null;
  skipReason?: string | null;
}

export interface ApprovalSubjectSummary {
  number?: string | null;
  party?: string | null;
  amount?: number | null;
  currency?: string | null;
  date?: string | null;
  description?: string | null;
  lines: { description: string; quantity?: number | null; amount?: number | null }[];
  deepLink?: string | null;
}

export interface ApprovalRequestDetail {
  request: ApprovalRequestRow;
  steps: ApprovalRequestStep[];
  summary?: ApprovalSubjectSummary | null;
  canDecide: boolean;
  canCancel: boolean;
  /** FW-3B: an administrator may cancel this stuck request. */
  canAdminCancel?: boolean;
}

export interface SubjectApproval {
  subjectType: string;
  subjectId: string;
  request?: ApprovalRequestDetail | null;
}

export interface ApprovalListFilter {
  status?: ApprovalRequestStatus;
  subjectType?: string;
  search?: string;
  from?: string;
  to?: string;
}
